import { FormControlLabel, Typography } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { useFlag } from '@unleash/proxy-client-react'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import OutlinedButtons from 'app/componentV4/Button/OutlinedButtons'
import CheckBox from 'app/componentV4/CheckBox'
import GroupSelect from 'app/componentV4/GroupSelect'
import PhoneNumberInput from 'app/componentV4/Input/PhoneNumberInput'
import SimpleDatePicker from 'app/componentV4/Input/SimpleDatePicker'
import SimpleInput from 'app/componentV4/Input/SimpleInput'
import LabelError from 'app/componentV4/Labels/LabelError'
import LabelInput from 'app/componentV4/Labels/LabelInput'
import { CLOSE, SECONDARY } from 'app/constants/button'
import { CHANNEL_CALL, CHANNEL_EMAIL, CHANNEL_SMS } from 'app/constants/channel'
import clsx from 'clsx'
import { allLanguages } from 'commons/fetchLanguages'
import { useCountryCode } from 'commons/phoneNumber'
import { usePlace } from 'commons/usePlace'
import { Form, Formik } from 'formik'
import PropTypes from 'prop-types'
import { find, propEq } from 'ramda'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  errorCodeFromBackendForFields,
  getErrorMessageByErrorCode,
} from './helpers'
import { validationSchema } from './validation'

import PrimaryTooltip from 'app/componentV4/Tooltip/PrimaryTooltip'
import { InfoIcon } from 'app/components/icons'
import { DATE_PICKER_FORMAT } from 'libs/dateUtils'
import useStyles from './styles'
import useAddOneByOne from './useAddOneByOne'
import { disableSpaceBar } from 'commons/keyDown'

const preferredChannels = [
  {
    name: 'phoneNumber',
    countryCodeKey: 'countryCode',
    contract: null,
    label: 'message.credit.alert.dialog.sms',
    id: CHANNEL_SMS,
  },
  {
    name: 'email',
    contract: null,
    label: 'message.credit.alert.dialog.email',
    id: CHANNEL_EMAIL,
  },
  {
    name: 'automatedCallPhoneNumber',
    countryCodeKey: 'automatedCallCountryCode',
    contract: null,
    label: 'message.credit.alert.dialog.automateCall',
    id: CHANNEL_CALL,
  },
]

const defaultAutomatedCallCountryCode = 'us'
const defaultAutomatedCallDialCode = '+1'

const CreateSubscriberOneByOneForm = ({
  onClose,
  onFetch,
  setIsAddAnotherChecked,
  isSendInvitationChecked,
  setIsSendInvitationChecked,
  isShowAddAnother = false,
}) => {
  const { formatMessage, locale } = useIntl()
  const { countryCode } = useCountryCode()
  const classes = useStyles()

  const [smsAutomatedCall, setSmsAutomatedCall] = useState({
    smsDialCode: null,
    automatedDialCode: defaultAutomatedCallDialCode,
  })

  const place = usePlace()

  const { errorCode, isLoading, handleCreateSubscriber } = useAddOneByOne()

  const isInviteFlow = place?.isInviteSubscribers || false

  const initialValues = {
    automatedCallCountryCode: '',
    automatedCallPhoneNumber: '',
    birthday: '',
    countryCode,
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    preferChannel: '',
    preferLanguage: '',
    tags: [],
    uniqueId: '',
    sendInvitation: isSendInvitationChecked,
    addAnotherSubscriber: false,
  }

  const countryCodeRef = useRef({
    countryCode: '',
    automatedCallCountryCode: defaultAutomatedCallCountryCode,
  })

  const [langauge, setLangauge] = useState([])

  const getLanguages = async () => {
    const language = await allLanguages()
    setLangauge(language)
  }

  useEffect(() => {
    getLanguages()
  }, [])

  const resetForm = (formik) => {
    formik.resetForm({
      values: {
        ...initialValues,
        countryCode: countryCodeRef.current.countryCode,
        automatedCallCountryCode:
          countryCodeRef.current.automatedCallCountryCode,
      },
    })
  }

  const generateOptionPreferredChannel = ({ values, errors }) =>
    preferredChannels
      .filter(({ name, countryCodeKey }) => {
        const value = values[name]
        return value && !errors[name] && value !== values[countryCodeKey]
      })
      .map(({ name, ...rest }) => ({
        ...rest,
        name,
        contract: values[name],
      }))

  const maintainPhoneNumber = (formik) => {
    const { smsDialCode, automatedDialCode } = smsAutomatedCall
    if (smsDialCode) {
      formik.setFieldValue('countryCode', smsDialCode)
      formik.setFieldValue('phoneNumber', smsDialCode)
    }
    if (automatedDialCode) {
      formik.setFieldValue('automatedCallCountryCode', automatedDialCode)
      formik.setFieldValue('automatedCallPhoneNumber', automatedDialCode)
    }
  }

  const onSubmitted = async (values, formik) => {
    const result = await handleCreateSubscriber(values)

    if (values.addAnotherSubscriber && result) {
      resetForm(formik)
      formik.setFieldValue('addAnotherSubscriber', true)
      maintainPhoneNumber(formik)
    } else if (result) {
      onFetch()
    } else return
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema(countryCodeRef.current, formatMessage)}
      onSubmit={async (values, formikHelper) => {
        await onSubmitted(values, formikHelper)
      }}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        setFieldValue,
        validateForm,
        setFieldTouched,
      }) => {
        const preferSelectorOptions = generateOptionPreferredChannel({
          values,
          errors,
        })

        const preferChannelValue = useMemo(() => {
          return find(propEq('id', values.preferChannel), preferSelectorOptions)
        }, [values.preferChannel, preferSelectorOptions])

        const preferLanguageValue = useMemo(() => {
          return find(propEq('id', values.preferLanguage), langauge)
        }, [values.preferLanguage, langauge])

        useEffect(() => {
          if (values.preferChannel && !preferChannelValue) {
            setFieldValue('preferChannel', '')
          }
        }, [preferChannelValue, values.preferChannel, setFieldValue])

        useEffect(() => {
          validateForm()
        }, [locale])

        useEffect(() => {
          if (countryCode) {
            setFieldValue('countryCode', countryCode)
          }
        }, [countryCode])

        const handleChangePhoneNumber = useCallback(
          ({ value, dialCode, countryCode, key }) => {
            if (key === 'phoneNumber') {
              setFieldValue('countryCode', dialCode)
              setFieldValue('phoneNumber', value)
              countryCodeRef.current.countryCode = value
              setSmsAutomatedCall((prevState) => ({
                ...prevState,
                smsDialCode: dialCode,
              }))
            } else {
              setFieldValue('automatedCallCountryCode', dialCode)
              setFieldValue('automatedCallPhoneNumber', value)
              countryCodeRef.current.automatedCallCountryCode = countryCode
              setSmsAutomatedCall((prevState) => ({
                ...prevState,
                automatedDialCode: dialCode,
              }))
            }
          },
          [setFieldValue],
        )

        const getHelperText = (key, errorCode) => {
          if (touched[key] && errors[key]) {
            return errors[key]
          } else if (errorCode) {
            const errorMessage = getErrorMessageByErrorCode(
              key,
              Number(errorCode),
            )
            if (errorMessage) {
              return formatMessage({ id: errorMessage })
            }
          }

          return undefined
        }

        const isError = (key, errorCode) => {
          const hasFormikError = touched[key] && Boolean(errors[key])
          const hasBackendError = Boolean(
            errorCodeFromBackendForFields[errorCode],
          )
          return hasFormikError || hasBackendError
        }

        const preferedLangTooltip = (
          <PrimaryTooltip
            title={formatMessage({
              id: 'preferred.language.default.text',
            })}
          >
            <div>
              <InfoIcon
                className="w-20 flex"
                width="14"
                height="14"
                color="#183560"
              />
            </div>
          </PrimaryTooltip>
        )

        return (
          <Form className="h-full flex flex-col pt-10 relative">
            <div className="flex-grow overflow-y-auto px-4 min-h-0 pb-10">
              <div
                className={clsx(
                  classes.inputBox,
                  'grid grid-cols-1 sm:grid-cols-2',
                )}
              >
                <LabelInput
                  label={formatMessage({ id: 'firstName.label' })}
                  required
                >
                  <SimpleInput
                    className={classes.input}
                    id="firstName"
                    name="firstName"
                    placeholder={formatMessage({
                      id: 'firstName.label',
                    })}
                    value={values.firstName}
                    size="medium"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoFocus
                    tabIndex="1"
                    fullWidth
                    error={isError('firstName')}
                    helperText={getHelperText('firstName')}
                  />
                </LabelInput>
                <LabelInput
                  label={formatMessage({ id: 'lastName.label' })}
                  required
                >
                  <SimpleInput
                    className={classes.input}
                    id="lastName"
                    name="lastName"
                    placeholder={formatMessage({
                      id: 'lastName.label',
                    })}
                    value={values.lastName}
                    size="medium"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    tabIndex="2"
                    fullWidth
                    error={isError('lastName')}
                    helperText={getHelperText('lastName')}
                  />
                </LabelInput>

                <LabelInput
                  label={formatMessage({
                    id: 'membershipCards.table.group.label',
                  })}
                >
                  <GroupSelect
                    selectedTags={values.tags}
                    onTagsChange={(value) => setFieldValue('tags', value)}
                    tabIndex="3"
                  />
                </LabelInput>
                <LabelInput
                  label={formatMessage({ id: 'preferred.language.label' })}
                  infoComponent={preferedLangTooltip}
                >
                  <Autocomplete
                    options={langauge}
                    onChange={(_, selectedOptions) =>
                      setFieldValue('preferLanguage', selectedOptions?.id || '')
                    }
                    value={preferLanguageValue || ''}
                    noOptionsText={formatMessage({
                      id: 'crm.form.receiveMethods.option.empty',
                    })}
                    onBlur={handleBlur}
                    getOptionLabel={(option) =>
                      option ? `${option.nativeName} - ${option.name}` : ''
                    }
                    renderOption={(option) => (
                      <div>
                        <Typography className="text-12 text-primary ">
                          {option.nativeName}
                        </Typography>
                        <Typography className="text-12 text-primary font-bold">
                          {option.name}
                        </Typography>
                      </div>
                    )}
                    renderInput={(params) => (
                      <div ref={params.InputProps.ref} className="w-full">
                        <SimpleInput
                          id="preferLanguage"
                          className={classes.input}
                          placeholder={formatMessage({
                            id: 'preferred.language.label',
                          })}
                          endAdornment={
                            <div className={classes.endAdornment}>
                              {params.InputProps.endAdornment}
                            </div>
                          }
                          size="medium"
                          fullWidth
                          tabIndex="4"
                          error={isError('preferLanguage')}
                          helperText={getHelperText('preferLanguage')}
                          {...params}
                        />
                      </div>
                    )}
                  />
                </LabelInput>
                <LabelInput
                  label={formatMessage({ id: 'subscriber.label.uniqueId' })}
                >
                  <SimpleInput
                    className={classes.input}
                    id="uniqueId"
                    name="uniqueId"
                    placeholder={formatMessage({
                      id: 'subscriber.label.uniqueId',
                    })}
                    value={values.uniqueId}
                    size="medium"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    tabIndex="5"
                    error={isError('uniqueId', errorCode)}
                    helperText={getHelperText('uniqueId', errorCode)}
                  />
                </LabelInput>
                <LabelInput
                  label={formatMessage({
                    id: 'subscriber.label.dateOfBirth',
                  })}
                >
                  <SimpleDatePicker
                    id="birthday"
                    format={DATE_PICKER_FORMAT}
                    placeholder="MM/DD/YYYY"
                    maxDate={new Date()}
                    value={values.birthday}
                    autoComplete="off"
                    className={classes.input}
                    error={errors.birthday && touched.birthday}
                    helperText={errors.birthday}
                    onChange={(date) => {
                      setFieldValue('birthday', date)
                      setFieldTouched('birthday', true, false)
                    }}
                  />
                </LabelInput>
              </div>
              <div className="text-primary-300 font-medium text-[16px] leading-6 my-8">
                {place.enabledAutomatedCall ? (
                  <FormattedMessage id="subscriber.form.info.text2" />
                ) : (
                  <FormattedMessage id="subscriber.form.info.text" />
                )}
              </div>
              <div
                className={clsx(
                  classes.inputBox,
                  'grid grid-cols-1 sm:grid-cols-2',
                )}
              >
                <LabelInput
                  label={formatMessage({
                    id: 'crm.form.notificationMethod.sms',
                  })}
                >
                  <PhoneNumberInput
                    value={values.phoneNumber}
                    name="phoneNumber"
                    id="subscriber-input-phone-number"
                    onChange={(data, value) =>
                      handleChangePhoneNumber({
                        value,
                        dialCode: `+${data.dialCode}`,
                        countryCode: data.countryCode,
                        key: 'phoneNumber',
                      })
                    }
                    isShowDropdownTop={true}
                    country={values.countryCode || countryCode}
                    inputProps={{ required: false }}
                  />
                  {errorCodeFromBackendForFields[errorCode] && (
                    <LabelError
                      id={'subscriber-phoneNumber-error'}
                      text={formatMessage({
                        id: getErrorMessageByErrorCode(errorCode),
                      })}
                    />
                  )}
                </LabelInput>
                {!!place.enabledAutomatedCall && (
                  <LabelInput
                    label={formatMessage({
                      id: 'message.credit.alert.dialog.automateCall',
                    })}
                  >
                    <PhoneNumberInput
                      value={values.automatedCallPhoneNumber}
                      name="automatedCallPhoneNumber"
                      id="subscriber-input-automated-call-phone-number"
                      onChange={(data, value) =>
                        handleChangePhoneNumber({
                          value,
                          dialCode: `+${data.dialCode}`,
                          countryCode: data.countryCode,
                          key: 'automatedCallPhoneNumber',
                        })
                      }
                      isShowDropdownTop={true}
                      country={defaultAutomatedCallCountryCode}
                      defaultCountry={defaultAutomatedCallCountryCode}
                      onlyCountries={[defaultAutomatedCallCountryCode]}
                      inputProps={{ required: false }}
                    />
                    {errorCodeFromBackendForFields[errorCode] && (
                      <LabelError
                        id={'subscriber-automatedCallPhoneNumber-error'}
                        text={formatMessage({
                          id: getErrorMessageByErrorCode(errorCode),
                        })}
                      />
                    )}
                  </LabelInput>
                )}
                <LabelInput
                  label={formatMessage({
                    id: 'email.address.label',
                  })}
                >
                  <SimpleInput
                    id="email"
                    className={classes.input}
                    name="email"
                    placeholder={formatMessage({
                      id: 'email.address.label',
                    })}
                    value={values.email}
                    size="medium"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    inputProps={{
                      onKeyDown: (e) => {
                        disableSpaceBar(e)
                      },
                    }}
                    error={isError('email', errorCode)}
                    helperText={getHelperText('email', errorCode)}
                  />
                </LabelInput>
                <LabelInput
                  label={formatMessage({ id: 'crm.form.notificationMethod' })}
                  required
                >
                  <Autocomplete
                    options={preferSelectorOptions}
                    onChange={(_, selectedOptions) =>
                      setFieldValue('preferChannel', selectedOptions?.id || '')
                    }
                    value={preferChannelValue || ''}
                    getOptionLabel={(option) =>
                      option
                        ? `${formatMessage({ id: option.label })} : ${option.contract}`
                        : ''
                    }
                    noOptionsText={formatMessage({
                      id: 'crm.form.receiveMethods.option.empty',
                    })}
                    renderOption={(option) => (
                      <p>{`${formatMessage({ id: option.label })} : ${option.contract}`}</p>
                    )}
                    renderInput={(params) => (
                      <div ref={params.InputProps.ref} className="w-full">
                        <SimpleInput
                          id="preferChannel"
                          name="preferChannel"
                          className={classes.input}
                          placeholder={formatMessage({
                            id: 'crm.form.notificationMethod',
                          })}
                          endAdornment={
                            <div className={classes.endAdornment}>
                              {params.InputProps.endAdornment}
                            </div>
                          }
                          size="medium"
                          fullWidth
                          onBlur={handleBlur}
                          error={isError('preferChannel')}
                          helperText={getHelperText('preferChannel')}
                          {...params}
                        />
                      </div>
                    )}
                  />
                </LabelInput>
              </div>
            </div>
            <div className="sticky inset-x-0 bottom-0 bg-white border-t border-primary-350 py-5 px-4 z-5">
              <div className="flex flex-col sm:flex-row space-x-5 justify-between items-center">
                <div>
                  {isShowAddAnother ? (
                    <FormControlLabel
                      control={
                        <CheckBox
                          checked={values.addAnotherSubscriber}
                          onChange={(e) => {
                            handleChange(e)
                            setIsAddAnotherChecked(e.target.checked)
                          }}
                          name="addAnotherSubscriber"
                          className={classes.checkbox}
                        />
                      }
                      className="whitespace-nowrap text-primary text-14 m-0 font-normal"
                      label={formatMessage({
                        id: 'subscriber.addAnotherSubscriber.checkbox.label',
                      })}
                    />
                  ) : null}

                  {isInviteFlow ? (
                    <FormControlLabel
                      control={
                        <CheckBox
                          checked={values.sendInvitation}
                          onChange={(e) => {
                            handleChange(e)
                            setIsSendInvitationChecked(e.target.checked)
                          }}
                          name="sendInvitation"
                          className={classes.checkbox}
                        />
                      }
                      className="whitespace-nowrap text-primary text-14 m-0 font-normal"
                      label={formatMessage({
                        id: 'subscriber.addOneByOne.checkbox.label',
                      })}
                    />
                  ) : null}
                </div>

                <div className="flex flex-wrap gap-5">
                  <OutlinedButtons
                    id="close-add-subscriber-one-by-one-btn"
                    size="small"
                    className="h-36 w-full sm:w-auto"
                    color={CLOSE}
                    onClick={onClose}
                  >
                    <FormattedMessage id={'text.close'} />
                  </OutlinedButtons>
                  <ContainedButton
                    color={SECONDARY}
                    size="small"
                    type="submit"
                    isLoading={isLoading}
                    className="h-36 w-full sm:w-auto"
                    disabled={isLoading}
                  >
                    <FormattedMessage
                      id={'subscriber.dialog.createSubscriber'}
                    />
                  </ContainedButton>
                </div>
              </div>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

CreateSubscriberOneByOneForm.propTypes = {
  onClose: PropTypes.func,
  onFetch: PropTypes.func,
  setIsAddAnotherChecked: PropTypes.func,
}

export default memo(CreateSubscriberOneByOneForm)
